import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { Lock, CheckCircle } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            await api.post('/auth/reset-password', { token, new_password: password });
            setSuccess(true);
            toast.success('Password reset successfully');
            setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to reset password';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <Card className="border border-white/10 bg-white/5 backdrop-blur-md shadow-xl max-w-md w-full">
                <CardContent className="pt-6 text-center">
                    <h2 className="text-xl font-bold text-white mb-4">Invalid Reset Link</h2>
                    <p className="text-slate-400 mb-4">This password reset link is invalid or has expired.</p>
                    <Link to="/forgot-password">
                        <Button className="bg-gradient-to-r from-[#9b00e8] to-[#ff4500]">
                            Request New Link
                        </Button>
                    </Link>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <div className="flex flex-col items-center mb-6">
                <BrandLogo className="h-16 w-16 rounded-lg shadow-lg" />
                <h1 className="mt-4 text-2xl font-bold text-white">Create New Password</h1>
                <p className="mt-2 text-sm text-slate-300">Enter your new password below</p>
            </div>

            <Card className="border border-white/10 bg-white/5 backdrop-blur-md shadow-xl">
                <CardContent className="pt-6">
                    {success ? (
                        <div className="text-center py-4">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                                <CheckCircle className="h-8 w-8 text-green-400" />
                            </div>
                            <h3 className="text-lg font-medium text-white mb-2">Password Reset!</h3>
                            <p className="text-slate-400 mb-4">
                                Your password has been reset successfully. Redirecting to login...
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-200">New Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 bg-white/5 border-slate-600 text-white placeholder:text-slate-400"
                                        required
                                        minLength={8}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="text-slate-200">Confirm Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        placeholder="••••••••"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10 bg-white/5 border-slate-600 text-white placeholder:text-slate-400"
                                        required
                                        minLength={8}
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-gradient-to-r from-[#9b00e8] to-[#ff4500] hover:from-[#8800d4] hover:to-[#e63e00]"
                                disabled={loading}
                            >
                                {loading ? 'Resetting...' : 'Reset Password'}
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default ResetPassword;
